import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AutoSizer, MultiGrid } from 'react-virtualized'
import CustomTooltip from '../../../../_components/CustomTooltip'
import { useModal, useModalExtra } from '../../../../_components/Modal'
import { ChrofyCustomSelect } from '../../../common/ChrofyCustomSelect'
import { useModifyClassList } from '../../../../common/utils/CSSUtils'
import LoadingMessage from '../../../../_components/LoadingMessage'
import { loadFileFromIndexDb } from '../extra/services'
import { FILE_STATE, FILE_TYPE } from '../extra/types'
import { formatCommaDigit } from '../../../../common/utils/NumberUtils'
import { isReactNode } from '../../../../common/utils/ObjectUtils'
import { TABLE_CELL_WIDTH } from '../extra/constants'
import '../../../../assets/css/style.css'
import '../../../../assets/css/common.css'
import '../extra/types.js'

/**
 * ファイル内容表示（モーダル本体）
 *
 * @param {Object} props
 * @param {string} props.fileKey
 * @param {string} props.taskKey
 * @param {import('../extra/types').FileState} props.file
 * @param {function(string, string): Promise<void> | void} props.onChangeSheet
 */
export const FileContentsModal = ({
  fileKey,
  taskKey,
  file,
  onChangeSheet,
  headers,
  records,
  rowCount,
  selectedSheet,
  handleSheetChange,
  isLoading
}) => {
  const [measureKey, setMeasureKey] = useState(0)

  useEffect(() => {
    // 初回表示直後はメインスレッドが混雑し、モーダル幅(w-[1200px])が
    // レイアウト/描画される前に AutoSizer がコンテナ幅を実測してしまうことがある。
    // 結果テーブルが狭い幅で固定され横スクロール状態になるため、
    // 幅が確定する次フレーム以降に AutoSizer を再マウントして測り直す。
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setMeasureKey((k) => k + 1))
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [])

  // 先頭列は「行番号」、以降はデータ列（TABLE_CELL_WIDTH 固定幅）
  const columnWidth = ({ index }) => (index === 0 ? 100 : TABLE_CELL_WIDTH)

  // MultiGrid は列・行の両方を仮想化し、縦/横スクロールバーを
  // コンテナ（ビューポート）端に配置する。react-virtualized の Table は
  // 本体 Grid が「全列幅の単一カラム」構造となり、縦スクロールバーが
  // コンテンツ右端（画面外）に付いてしまうため MultiGrid を使用する。
  // rowIndex=0 はヘッダー行（fixedRowCount=1 で固定）、columnIndex=0 は行番号列。
  const cellRenderer = ({ columnIndex, rowIndex, key, style }) => {
    const isHeaderRow = rowIndex === 0
    const isRowNumberColumn = columnIndex === 0

    const cellClasses = isHeaderRow
      ? 'flex items-center !px-2 border border-[#d5d6e5] bg-[#e0e5f2] text-[#7476a6] text-sm font-bold'
      : 'flex items-center !px-2 border border-[#d5d6e5] bg-white text-sm'

    if (isHeaderRow) {
      const label = isRowNumberColumn ? '行番号' : headers[columnIndex - 1]
      return (
        <div key={key} style={style} className={cellClasses}>
          <span className="truncate" title={label || '-'}>
            {label || '-'}
          </span>
        </div>
      )
    }

    if (isRowNumberColumn) {
      return (
        <div key={key} style={style} className={cellClasses}>
          {rowIndex}
        </div>
      )
    }

    const value = records[rowIndex - 1]?.[columnIndex - 1]
    return (
      <div key={key} style={style} className={cellClasses}>
        {isReactNode(value) ? (
          <span className="truncate" title={String(value)}>
            {String(value)}
          </span>
        ) : (
          ''
        )}
      </div>
    )
  }

  const sheetSelect = useMemo(() => {
    if (file.fileType !== FILE_TYPE.EXCEL || !file.sheetList || file.sheetList.length === 0) return null

    // S3からダウンロード済みのExcelは既にCSV形式のためシート選択は非活性
    if (!file.isUploadedLocally || file.fileState === FILE_STATE.VIRUS_CHECK_IN_PROGRESS) {
      return (
        <CustomTooltip title={file.sheetName || 'シート1'}>
          {(ref) => (
            <div
              ref={ref}
              className="px-3 py-1.5 border border-blue2-lighten-40 rounded-md text-sm bg-blue2-lighten-50 opacity-60 w-[150px] truncate text-blue2-darken-20"
            >
              {file.sheetName || 'シート1'}
            </div>
          )}
        </CustomTooltip>
      )
    }

    return (
      <ChrofyCustomSelect
        value={selectedSheet}
        onChange={handleSheetChange}
        options={file.sheetList.map((name) => ({ label: name, value: name }))}
        className="w-[150px]"
        selectClassName="!py-1.5 !rounded-md !bg-white"
        // 下の MultiGrid の固定列（行番号・ヘッダー、z-10）と重なり順が同値になると
        // DOM 順で後のグリッドが手前に描画され選択肢が隠れるため、明示的に上へ出す
        dropdownClassName="!z-20"
        hasNoSelect={false}
        dropdownPlacement="inline"
      />
    )
  }, [
    file.fileState,
    file.fileType,
    file.isUploadedLocally,
    file.sheetList,
    file.sheetName,
    handleSheetChange,
    selectedSheet
  ])

  return (
    <div>
      <div className="text-caption-2 grey--text mb-3">
        {isLoading ? (
          'データを読み込んでいます...'
        ) : (
          <>
            データ行数: {formatCommaDigit(String(rowCount))}行 / 列数: {headers.length}列
          </>
        )}
      </div>

      {/* シート選択（Excelファイルの場合） */}
      {sheetSelect && (
        <div className="mb-4">
          <label className="text-caption-2 grey--text mb-1 block">シート選択</label>
          {sheetSelect}
        </div>
      )}

      <div className="setting-table is-benchmark h-[500px] w-full min-w-0 overflow-hidden relative">
        {isLoading ? (
          <LoadingMessage message="読み込み中..." minHeightClassName="min-h-full h-full" />
        ) : (
          <div className="h-full w-full min-h-0 min-w-0">
            <AutoSizer key={measureKey}>
              {({ height, width }) => (
                <MultiGrid
                  key={`${selectedSheet}-${headers.length}-${records.length}`}
                  className="setting-table__table"
                  fixedRowCount={1}
                  fixedColumnCount={1}
                  cellRenderer={cellRenderer}
                  columnCount={headers.length + 1}
                  columnWidth={columnWidth}
                  rowCount={records.length + 1}
                  rowHeight={30}
                  width={width}
                  height={height}
                  overscanColumnCount={5}
                  overscanRowCount={20}
                  classNameBottomLeftGrid="border-r border-blue2-lighten-40 z-10"
                  classNameTopLeftGrid="border-r border-blue2-lighten-40 z-10"
                />
              )}
            </AutoSizer>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * ファイル内容表示モーダルを開くフック
 *
 * @param {function(string, string): void} onChangeSheet
 * @param {string} taskKey
 * @returns {function(string, import('../extra/types').FileState): Promise<void>}
 */
export const useFileContentsModal = (onChangeSheet, taskKey) => {
  const showModal = useModal()
  const { promisify } = useModifyClassList('.modal-window', ['w-[1200px]', 'max-w-[95svw]'])

  const openFileContentsModal = (fileKey, file) => {
    return promisify((res) => {
      const ModalWrapper = () => {
        const [selectedSheet, setSelectedSheet] = useState(file.sheetName || '')
        const [headers, setHeaders] = useState([])
        const [records, setRecords] = useState([])
        const [rowCount, setRowCount] = useState(0)
        // 大容量ファイルはIndexedDBからの全行読み出しに分単位かかるため、完了まで読み込み中表示を出す
        const [isLoading, setIsLoading] = useState(true)

        const loadLatest = useCallback(async () => {
          setIsLoading(true)
          try {
            const fileData = await loadFileFromIndexDb(fileKey, taskKey)
            const rows = fileData?.rows || []
            setHeaders(fileData?.headers || [])
            setRecords(rows)
            // チャンクにはデータ行のみ保存されているため、読み出し済みの行数がそのままデータ行数
            setRowCount(rows.length)
          } catch (error) {
            console.error('Failed to load file content:', error)
            setRowCount(0)
          } finally {
            setIsLoading(false)
          }
        }, [])

        useEffect(() => {
          loadLatest().catch(console.error)
        }, [loadLatest])

        const handleSheetChange = useCallback(
          async (e) => {
            const newSheetName = e.target.value
            setSelectedSheet(newSheetName)
            await onChangeSheet?.(fileKey, newSheetName)
            await loadLatest()
          },
          [loadLatest]
        )

        return (
          <FileContentsModal
            fileKey={fileKey}
            taskKey={taskKey}
            file={file}
            onChangeSheet={onChangeSheet}
            headers={headers}
            records={records}
            rowCount={rowCount}
            selectedSheet={selectedSheet}
            handleSheetChange={handleSheetChange}
            isLoading={isLoading}
          />
        )
      }

      showModal({
        title: <h2 className="px-5">ファイル内容: {file.fileName}</h2>,
        description: null,
        isCancel: false,
        isWide: false,
        isSpinnerDisabled: true,
        modalStyle: {
          maxHeight: 'calc(100dvh - 32px)',
          overflowY: 'auto'
        },
        onClose: res,
        body: <ModalWrapper />,
        button: [
          {
            name: '閉じる',
            className: 'footer-btn button_base',
            click: () => {
              res()
            },
            close: true
          }
        ]
      })
    })
  }

  return openFileContentsModal
}
